import { useState } from 'react';
import { useStudentPatterns } from '../../../api/hooks';
import type { TaskCompletion } from '../../../api/types';
import Box from '../components/Box';
import { FAILED, Figure, LOADING, PctBar, SubjectPills, type SubjectKey, pct } from './shared';

const TASK_LABEL = { worksheet: 'Worksheets', homework: 'Homework', exam: 'Assigned exams' } as const;

function TaskRow({ label, t }: { label: string; t: TaskCompletion }) {
  const donePct = t.assigned ? (t.done / t.assigned) * 100 : null;
  return (
    <li className="space-y-1.5">
      <div className="flex flex-wrap justify-between gap-2 text-[13px]">
        <span className="font-semibold text-ink">{label}</span>
        <span className="font-mono text-ink">{t.done} of {t.assigned} done
          <span className="text-muted">{t.late ? ` · ${t.late} late` : ''}{t.missing ? ` · ${t.missing} missing` : ''}{t.avg_score != null ? ` · avg ${pct(t.avg_score)}` : ''}</span></span>
      </div>
      <PctBar value={donePct} tone={t.missing ? 'danger' : 'success'} label={`${label}: ${t.done} of ${t.assigned} done`} />
    </li>
  );
}

/** Practice next to real exams, and how reliably assigned work is done. */
export default function PracticeView({ id, studentName }: { id: number; studentName: string }) {
  const [subject, setSubject] = useState<SubjectKey>('all');
  const q = useStudentPatterns(id, subject);
  const p = q.data;
  const gap = p && p.practice.practice_avg != null && p.practice.exam_avg != null ? p.practice.practice_avg - p.practice.exam_avg : null;
  const tasks = p ? (Object.keys(TASK_LABEL) as (keyof typeof TASK_LABEL)[]).filter((k) => p.tasks[k].assigned > 0) : [];

  return (
    <div className="space-y-4">
      <SubjectPills id={id} value={subject} onChange={setSubject} />
      <Box title="Practice vs exams" help="Average score on practice (study-mode) runs next to real exams. A big gap points to exam stress, or to practising different things from what is examined.">
        {q.isLoading ? LOADING : q.isError || !p ? FAILED : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Figure label="Practice average" value={pct(p.practice.practice_avg, 1)} sub={`${p.practice.practice_count} practice ${p.practice.practice_count === 1 ? 'run' : 'runs'}`} />
              <Figure label="Exam average" value={pct(p.practice.exam_avg, 1)} sub={`${p.practice.exam_count} ${p.practice.exam_count === 1 ? 'exam' : 'exams'}`} />
              <Figure label="Difference" value={gap == null ? '—' : `${gap >= 0 ? '+' : ''}${gap.toFixed(1)} pts`}
                sub={gap == null ? 'Needs both practice and exams' : gap > 10 ? 'Does better in practice' : gap < -10 ? 'Does better in exams' : 'About the same'} />
            </div>
            {gap != null && gap > 15 && (
              <p className="mt-3 rounded-[12px] bg-warning-bg px-3 py-2 text-[13px] text-warning-ink">
                {studentName} scores much higher in practice than in exams. Exam nerves, time pressure or practising easier material are worth checking.
              </p>
            )}
            {p.practice.practice_count === 0 && <p className="mt-3 text-[13px] text-muted">No practice runs yet: practice in the app shows here.</p>}
          </>
        )}
      </Box>
      <Box title="Assigned work" help="Worksheets, homework and exams set for this student: done, late (submitted after the due date) and missing (past due, never done).">
        {q.isLoading ? LOADING : q.isError || !p ? FAILED :
         tasks.length === 0 ? <p className="text-sm text-muted">Nothing has been assigned to {studentName} yet.</p> : (
           <ul className="space-y-4">{tasks.map((k) => <TaskRow key={k} label={TASK_LABEL[k]} t={p.tasks[k]} />)}</ul>
         )}
      </Box>
    </div>
  );
}
